import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import Drawer from "@/components/ui/drawer";
import Input from "@/components/ui/input";
import Dropdown from "@/components/ui/dropdown";
import Textarea from "@/components/ui/textarea";
import {
  Eye as EyeIcon,
  Actions as ActionsIcon,
  Edit as EditIcon,
  Pdf as PdfIcon,
  Download as DownloadIcon,
} from "@/components/icons";
import type { BlackListEntry } from "../types/black-list.types";
import styles from "./BlackListDetailDrawer.module.scss";

const buildReasonOptions = (t: (k: string) => string) => [
  { label: t("blackList.absence"), value: "Devamsızlık" },
  { label: t("blackList.failure"), value: "Başarısızlık" },
];

interface BlackListDetailDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  entry: BlackListEntry | null;
  onEdit?: (entry: BlackListEntry) => void;
  onRemoveFromBlackList?: (entry: BlackListEntry) => void;
}

const BlackListDetailDrawer: React.FC<BlackListDetailDrawerProps> = ({
  isOpen,
  onClose,
  entry,
  onEdit,
  onRemoveFromBlackList,
}) => {
  const { t } = useTranslation();
  const REASON_OPTIONS = buildReasonOptions(t);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    if (menuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [menuOpen]);

  if (!entry) return null;

  const headerTitle = (
    <span className={styles.headerTitle}>
      <EyeIcon width={24} height={24} color="#14181F" />
      {t("blackList.detail.title")}
    </span>
  );

  const hasActions = onEdit || onRemoveFromBlackList;
  const headerActions = hasActions ? (
    <div className={styles.menuWrapper} ref={menuRef}>
      <button
        type="button"
        className={styles.menuButton}
        onClick={() => setMenuOpen((prev) => !prev)}
        aria-haspopup="true"
        aria-expanded={menuOpen}
      >
        <ActionsIcon width={20} height={20} />
      </button>
      {menuOpen && (
        <div className={styles.menuDropdown}>
          {onEdit && (
            <button
              type="button"
              className={styles.menuItem}
              onClick={() => {
                setMenuOpen(false);
                onEdit(entry);
                onClose();
              }}
            >
              <EditIcon width={18} height={18} />
              {t("blackList.actions.edit")}
            </button>
          )}
          {onRemoveFromBlackList && (
            <button
              type="button"
              className={styles.menuItem}
              onClick={() => {
                setMenuOpen(false);
                onRemoveFromBlackList(entry);
                onClose();
              }}
            >
              <EditIcon width={18} height={18} />
              {t("blackList.actions.removeFromList")}
            </button>
          )}
        </div>
      )}
    </div>
  ) : undefined;

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      title={headerTitle}
      headerActions={headerActions}
      width="561px"
      position="right"
    >
      <div className={styles.drawerContent}>
        {/* Row 1: T.C. Kimlik No | Personel Ad Soyad */}
        <div className={styles.twoColumnRow}>
          <div className={styles.fieldGroup}>
            <label>{t("blackList.form.tcKimlikNo")}</label>
            <Input
              readOnly
              value={entry.tcKimlikNo ?? "-"}
              variant="outlined"
            />
          </div>
          <div className={styles.fieldGroup}>
            <label>{t("blackList.form.personnelName")}</label>
            <Input
              readOnly
              value={entry.personnelName ?? "-"}
              variant="outlined"
            />
          </div>
        </div>
        {/* Row 2: Başlangıç Tarihi | Bitiş Tarihi */}
        <div className={styles.twoColumnRow}>
          <div className={styles.fieldGroup}>
            <label>{t("blackList.form.startDate")}</label>
            <Input readOnly value={entry.startDate} variant="outlined" />
          </div>
          <div className={styles.fieldGroup}>
            <label>{t("blackList.form.endDate")}</label>
            <Input readOnly value={entry.endDate} variant="outlined" />
          </div>
        </div>
        {/* Row 3: Nedeni (full width) - dropdown */}
        <div className={styles.fieldGroup}>
          <label>{t("blackList.form.reason")}</label>
          <Dropdown
            options={REASON_OPTIONS}
            value={entry.reason}
            selectedLabel={entry.reason}
            onSelect={() => {}}
            disabled
          />
        </div>
        {/* Row 4: Açıklama (full width) - text area */}
        <div className={styles.fieldGroup}>
          <label>{t("blackList.form.description")}</label>
          <Textarea
            readOnly
            value={entry.description ?? ""}
            variant="outlined"
            rows={4}
          />
        </div>
        {/* Row 5: Destekleyici Belgeler (full width) - files.downloadUrl ile indirme linki */}
        {((entry.files && entry.files.length > 0) ||
          (entry.supportingDocuments && entry.supportingDocuments.length > 0)) && (
          <div className={styles.fieldGroup}>
            <label>
              {t("blackList.form.supportingDocuments")}
            </label>
            <div className={styles.documentsList}>
              {entry.files?.map((file) => (
                <div key={file.id} className={styles.documentItem}>
                  <PdfIcon
                    width={24}
                    height={24}
                    className={styles.documentIcon}
                  />
                  <div>
                    <div className={styles.documentInfo}>{file.originalName}</div>
                    {file.size != null && (
                      <div className={styles.documentSize}>
                        {(file.size / 1024).toFixed(1)} KB
                      </div>
                    )}
                  </div>
                  <a
                    href={file.downloadUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.downloadBtn}
                    title={t("common.download")}
                  >
                    <DownloadIcon width={20} height={20} />
                  </a>
                </div>
              ))}
              {!entry.files?.length &&
                entry.supportingDocuments?.map((doc, idx) => (
                  <div key={idx} className={styles.documentItem}>
                    <PdfIcon
                      width={24}
                      height={24}
                      className={styles.documentIcon}
                    />
                    <div>
                      <div className={styles.documentInfo}>{doc.name}</div>
                      {doc.size && (
                        <div className={styles.documentSize}>{doc.size}</div>
                      )}
                    </div>
                    <span className={styles.downloadBtn}>
                      <DownloadIcon width={20} height={20} />
                    </span>
                  </div>
                ))}
            </div>
          </div>
        )}
      </div>
    </Drawer>
  );
};

export default BlackListDetailDrawer;
